import { memo, useEffect, useRef } from "react";

import { formatTime } from "../../format";

/**
 * ProgressBar — the seek bar, fully imperative for performance.
 *
 * No React state: played/buffered fills move via `transform: scaleX` and the
 * thumb via `translateX`, written straight to the DOM from a rAF loop that only
 * runs while the video plays (plus one write per seek/pause). Re-rendering the
 * player tree per frame would melt scrolling on low-end machines; this keeps
 * playback at zero React work.
 */
function ProgressBar({ videoRef }) {
    const trackRef = useRef(null);
    const playedRef = useRef(null);
    const bufferedRef = useRef(null);
    const thumbRef = useRef(null);
    const bubbleRef = useRef(null);

    const widthRef = useRef(0);
    const draggingRef = useRef(false);

    useEffect(() => {
        const video = videoRef.current;
        const track = trackRef.current;
        if (!video || !track) return;

        let rafId = null;

        const measure = () => {
            widthRef.current = track.getBoundingClientRect().width;
        };

        const paint = () => {
            if (!video.duration) return;
            const fraction = video.currentTime / video.duration;

            if (playedRef.current) playedRef.current.style.transform = `scaleX(${fraction})`;
            if (thumbRef.current) thumbRef.current.style.transform = `translateX(${fraction * widthRef.current}px)`;

            const { buffered } = video;
            if (bufferedRef.current && buffered.length) {
                // Paint up to the end of the range the playhead sits in.
                let end = 0;
                for (let i = 0; i < buffered.length; i++) {
                    if (buffered.start(i) <= video.currentTime + 0.5) end = Math.max(end, buffered.end(i));
                }
                bufferedRef.current.style.transform = `scaleX(${end / video.duration})`;
            }
        };

        const loop = () => {
            paint();
            rafId = requestAnimationFrame(loop);
        };

        const startLoop = () => {
            if (rafId === null) rafId = requestAnimationFrame(loop);
        };

        const stopLoop = () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
            paint();
        };

        measure();
        paint();

        const resizeObserver = new ResizeObserver(() => {
            measure();
            paint();
        });
        resizeObserver.observe(track);

        video.addEventListener("play", startLoop);
        video.addEventListener("pause", stopLoop);
        video.addEventListener("ended", stopLoop);
        video.addEventListener("seeked", paint);
        video.addEventListener("loadedmetadata", paint);
        video.addEventListener("progress", paint);

        if (!video.paused) startLoop();

        return () => {
            stopLoop();
            resizeObserver.disconnect();
            video.removeEventListener("play", startLoop);
            video.removeEventListener("pause", stopLoop);
            video.removeEventListener("ended", stopLoop);
            video.removeEventListener("seeked", paint);
            video.removeEventListener("loadedmetadata", paint);
            video.removeEventListener("progress", paint);
        };
    }, [videoRef]);

    // ── Scrub interactions ───────────────────────────────────────────────
    const fractionFromEvent = (event) => {
        const rect = trackRef.current.getBoundingClientRect();
        return Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    };

    const seekToFraction = (fraction) => {
        const video = videoRef.current;
        if (video?.duration) video.currentTime = fraction * video.duration;
    };

    const moveBubble = (event) => {
        const video = videoRef.current;
        const bubble = bubbleRef.current;
        if (!video?.duration || !bubble) return;
        const fraction = fractionFromEvent(event);
        bubble.style.transform = `translateX(${fraction * widthRef.current}px) translateX(-50%)`;
        bubble.textContent = formatTime(fraction * video.duration);
    };

    const handlePointerDown = (event) => {
        event.stopPropagation();
        draggingRef.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        seekToFraction(fractionFromEvent(event));
    };

    const handlePointerMove = (event) => {
        moveBubble(event);
        if (draggingRef.current) seekToFraction(fractionFromEvent(event));
    };

    const handlePointerUp = (event) => {
        draggingRef.current = false;
        event.currentTarget.releasePointerCapture(event.pointerId);
    };

    return (
        <div
            ref={trackRef}
            className="player-progress"
            role="slider"
            aria-label="Progresso do vídeo"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onClick={(event) => event.stopPropagation()}
        >
            <div className="player-progress__well">
                <div ref={bufferedRef} className="player-progress__buffered" />
                <div ref={playedRef} className="player-progress__played" />
            </div>
            <div ref={thumbRef} className="player-progress__thumb" />
            <div ref={bubbleRef} className="player-progress__bubble" />
        </div>
    );
}

export default memo(ProgressBar);
